import { useDeviceType } from "@dash/mq";
import { Spinner } from "@dash/spinner";
import { useTheme } from "@emotion/react";
import axios from "axios";
import heic2any from "heic2any";
import React, { useCallback, useEffect, useState } from "react";
import { MdOutlineInsertDriveFile } from "react-icons/md";
import { pdfjs } from "react-pdf";

import { AttachmentDto, DraftAttachmentDto } from "@megaron/invoices-contracts";

import { KsefInvoiceView } from "./KsefInvoiceView";
import { PdfViewer } from "./PdfViewer";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type Props = {
  attachment: AttachmentDto | DraftAttachmentDto;
  showPreviewFunction: () => void;
  ksefNumber: string | null;
};

export const SUPPORTED_TYPES = ["text/xml", "application/pdf", "image/jpeg", "image/png", "image/heic"];

const areEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.attachment.url === nextProps.attachment.url &&
    prevProps.attachment.fileType === nextProps.attachment.fileType
  );
};
export const FilePreview: React.FC<Props> = React.memo(({ attachment, showPreviewFunction, ksefNumber }) => {
  const theme = useTheme();
  const { isMobile } = useDeviceType();

  const [file, setFile] = useState<Uint8Array | string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const url = attachment.url;
  const fileType = attachment.fileType;

  const fetchFile = useCallback(async () => {
    if (!url || file) return;

    try {
      setLoading(true);
      const response = await axios({ url, responseType: "arraybuffer" });
      const data = new Uint8Array(response.data);

      if (fileType === "image/heic") {
        try {
          const blob = new Blob([data.buffer], { type: "image/heic" });
          const convertedBlob = await heic2any({ blob, toType: "image/jpeg" });
          const reader = new FileReader();
          reader.readAsDataURL(convertedBlob as Blob);
          reader.onloadend = () => {
            setFile(reader.result as string);
          };
        } catch (conversionError) {
          // eslint-disable-next-line no-console
          console.error("HEIC conversion error:", conversionError);
          setError("Nie udało się załadować podglądu HEIC.");
        }
      } else if (fileType === "application/pdf" || fileType === "text/xml") {
        setFile(data);
      } else {
        setFile(url);
      }
      setLoading(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Fetch error:", err);
      setError("Nie udało się załadować podglądu");
      setLoading(false);
    }
  }, [url, file, fileType]);

  useEffect(() => {
    fetchFile();
  }, [fetchFile]);

  if (!SUPPORTED_TYPES.includes(fileType)) {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: theme.colors.primaryLight,
          padding: "10px",
          height: isMobile ? "180px" : "auto",
        }}
      >
        <MdOutlineInsertDriveFile size={30} />
      </div>
    );
  }

  if (fileType === "application/pdf") {
    return (
      <PdfViewer file={file as Uint8Array} showPreviewFunction={showPreviewFunction} error={error} loading={loading} />
    );
  }

  if (fileType === "text/xml" && ksefNumber) {
    return (
      <KsefInvoiceView
        ksefNumber={ksefNumber}
        xml={file as Uint8Array}
        showPreviewFunction={showPreviewFunction}
        error={error}
        loading={loading}
      />
    );
  }

  if (fileType === "text/xml" && !ksefNumber) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={showPreviewFunction}
        onKeyDown={(e) => e.key === "Enter" && showPreviewFunction()}
        css={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: theme.colors.primaryLight,
          padding: "16px",
          height: isMobile ? "180px" : "200px",
          cursor: "pointer",
          background: theme.colors.primaryLight,
        }}
      >
        <MdOutlineInsertDriveFile size={40} />
        <span css={{ marginTop: "8px", fontSize: "12px", textAlign: "center" }}>
          Faktura XML – kliknij, aby zobaczyć treść
        </span>
      </div>
    );
  }

  if (loading || !file) {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "180px",
          width: "100%",
        }}
      >
        <Spinner color={theme.colors.primary} size="28px" />
      </div>
    );
  }

  return (
    <img
      src={file as string}
      css={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "180px",
        width: "auto",
        objectFit: "cover",
        objectPosition: "top",
        cursor: "pointer",
      }}
      alt=""
      onClick={showPreviewFunction}
    />
  );
}, areEqual);
